// Positions still open in synced Interactive Brokers and Charles Schwab accounts (swing trades),
// from the last sync (GET /api/positions, schwab.js). A closed position becomes a normal trade; until then it shows here.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { useI18n } from './i18n';
import { currentLocale } from './langState';
import { formatMoney } from './pnl';

const price = (n) => (Number.isFinite(n) && n ? n.toLocaleString(currentLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : '—');
// IBKR's report dates look like "20261026".
const reportDay = (d) => {
  const m = String(d || '').match(/^(\d{4})-?(\d{2})-?(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' }) : '';
};

// The open positions, priced live: when the member's Schwab can quote them (schwab.js), they're
// asked for again every 15 seconds while the page is in view, so the P&L moves with the market.
const LIVE_SECONDS = 15;
export function useOpenPositions(skip) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (skip) return undefined;
    let live = true;
    let timer = null;
    const load = () => api('/positions?live=1').then((d) => {
      if (!live) return;
      setData(d);
      if (d.liveAt) timer = setTimeout(tick, LIVE_SECONDS * 1000);
    }).catch(() => {});
    const tick = () => (document.hidden ? (timer = setTimeout(tick, LIVE_SECONDS * 1000)) : load());
    load();
    return () => { live = false; clearTimeout(timer); };
  }, [skip]);
  return data;
}

// `data`: positions the page already loads (Accounts), instead of loading them again here.
export default function OpenPositions({ user, accounts, data: given }) {
  const { t } = useI18n();
  const own = useOpenPositions(user.isDemo || given !== undefined);
  const data = given !== undefined ? given : own;
  const positions = (data && data.positions) || [];
  if (!positions.length) return null;
  const nameOf = (id) => (accounts.find((a) => a._id === id) || {}).name || '';
  const markDay = reportDay((positions.find((p) => p.markDate) || {}).markDate);
  const ibkr = positions.some((p) => p.source !== 'schwab' && !p.live);
  const liveAt = data.liveAt ? new Date(data.liveAt).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit', second: '2-digit' }) : '';
  const schwabAt = data.schwabAt ? new Date(data.schwabAt).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' }) : '';
  const days = (d) => (d ? Math.max(0, Math.floor((Date.now() - new Date(d)) / 86400000)) : null);
  const total = positions.reduce((s, p) => s + (Number(p.unrealized) || 0), 0);
  const hasMarks = positions.some((p) => p.markPrice);

  return (
    <section className="home-section open-positions" aria-labelledby="open-positions-title">
      <div className="home-heading-row">
        <h2 className="home-heading" id="open-positions-title">{t('Open positions')} <span className="op-count">{positions.length}</span>
          {data.liveAt && data.marketOpen && <span className="op-live"><i aria-hidden="true" />{t('Live now')}</span>}</h2>
        {hasMarks && <span className={`op-total ${total >= 0 ? 'gain' : 'loss'}`}>{t('Unrealized {amount}', { amount: formatMoney(total) })}</span>}
      </div>
      <div className="op-wrap">
        <table className="op-table">
          <thead>
            <tr>
              <th scope="col">{t('Symbol')}</th>
              <th scope="col">{t('Side')}</th>
              <th scope="col" className="num">{t('Size')}</th>
              <th scope="col" className="num">{t('Avg entry')}</th>
              {hasMarks && <th scope="col" className="num">{t('Last price')}</th>}
              {hasMarks && <th scope="col" className="num">{t('Unrealized')}</th>}
              <th scope="col" className="num">{t('Held')}</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((p) => {
              const held = days(p.openedAt);
              return (
                <tr key={`${p.accountId}-${p.symbol}`}>
                  <th scope="row"><strong>{p.symbol}</strong><small>{nameOf(p.accountId)}</small></th>
                  <td><span className={`op-side ${p.side === 'Long' ? 'long' : 'short'}`}>{t(p.side)}</span></td>
                  <td className="num">{p.size.toLocaleString(currentLocale())}</td>
                  <td className="num">{price(p.entryPrice)}</td>
                  {hasMarks && <td className="num">{price(p.markPrice)}</td>}
                  {hasMarks && <td className={`num ${p.unrealized > 0 ? 'gain' : p.unrealized < 0 ? 'loss' : ''}`}>{p.markPrice ? formatMoney(p.unrealized || 0) : '—'}</td>}
                  <td className="num">{held === null ? '—' : held === 0 ? t('Today') : t(held === 1 ? '{n} day' : '{n} days', { n: held })}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="field-hint op-note">
        {ibkr && (hasMarks && markDay ? t('Prices as of IBKR’s close on {day}.', { day: markDay }) : t('Add the Open Positions section to your IBKR Flex Query to see the last price and unrealized P&L.'))}{' '}
        {liveAt ? t(data.marketOpen ? 'Live prices from Schwab, updated {time}.' : 'Market closed: last prices from Schwab, after hours included, at {time}.', { time: liveAt })
          : schwabAt && t('Schwab prices from the last sync at {time}.', { time: schwabAt })}{' '}
        {t('A position becomes a trade in your journal when it’s fully closed.')}{' '}
        <Link to="/app/accounts">{t('Accounts')} &rarr;</Link>
      </p>
    </section>
  );
}
